/**
 * features/exams/ExamTimetablePane.tsx — the series, day by day, a CARD PER
 * PAPER.
 * ------------------------------------------------------------------
 * What a teacher wants off this screen is not their own timetable (pupils get
 * that) but the school's: which days are exam days, what is sat when, and how
 * many candidates are in the building. So it is one panel per DAY, a row per
 * session, and a card for every paper in it.
 *
 * LKC (30 Oct 2026), from Al-Nur's pane of the same name, WITHOUT the
 * invigilation rota: Al-Nur's cards also carry each paper's FCS window and let
 * the exams officer re-time a paper on the rota's documents. The rota needs the
 * lesson timetable, which LKC does not have yet (TIMETABLE-PLAN.md), so this
 * pane is read-only until the rota comes across. The school's own exams run
 * three sessions — morning, mid-morning, afternoon — and each paper carries its
 * own times, so the cards show the paper's time rather than the session's.
 *
 * Seeing is every teacher's (canViewExams).
 */
import { useMemo, useState } from 'react';
import { CheckCircle2, Clock, EyeOff } from 'lucide-react';
import { SoftCard } from '@/components/ui/design';
import { gaboroneInstant, sessionName, type ExamSessionLoad } from '@/lib/examTimetable';
import type { ExamRunData } from './ExamsHub';
import { shortDay } from './ExamsHub';

/** By syllabus code: Cambridge's AS and A Level syllabuses are 9xxx, IGCSE 0xxx. */
const levelOfCode = (code: string): 'igcse' | 'alevel' => (code.startsWith('9') ? 'alevel' : 'igcse');
/** "Form 3 M" → '3'; a sixth-form class keeps its name. */
const formOfClass = (c: string) => /^Form\s*(\d)/i.exec(c)?.[1] ?? c;

export default function ExamTimetablePane({ run, now }: { run: ExamRunData; now: number }) {
  const { series, loads, loading } = run;
  const internal = series?.kind === 'internal';
  /* Cambridge series: filter by level (Form 5 and the sixth form are read by
     different people). The school's own exams: filter by form. */
  const [filter, setFilter] = useState('all');

  const forms = useMemo(
    () => [...new Set(loads.flatMap((l) => l.papers.flatMap((p) => p.classes.map(formOfClass))))].sort(),
    [loads],
  );
  const keep = (p: ExamSessionLoad['papers'][number]) =>
    filter === 'all' || (internal ? p.classes.some((c) => formOfClass(c) === filter) : levelOfCode(p.code) === filter);

  /* Day → its sessions. `sessionLoads` is already in the order the school
     runs them, so grouping keeps that order without sorting again. */
  const days = useMemo(() => {
    const out: { date: string; sessions: ExamSessionLoad[] }[] = [];
    const filtered = filter === 'all' ? loads : loads
      .map((l) => ({ ...l, papers: l.papers.filter(keep) }))
      .filter((l) => l.papers.length > 0);
    for (const load of filtered) {
      const last = out[out.length - 1];
      if (last && last.date === load.date) last.sessions.push(load);
      else out.push({ date: load.date, sessions: [load] });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loads, filter, internal]);

  if (loading) return <p className="py-10 text-sm text-[var(--sp-muted)]">Loading the timetable…</p>;
  if (!series) {
    return (
      <SoftCard pad="">
        <p className="px-5 py-10 text-center text-sm text-[var(--sp-muted)]">No exam series has been set up yet.</p>
      </SoftCard>
    );
  }

  const choices: [string, string][] = internal
    ? [['all', 'All forms'], ...forms.map((f): [string, string] => [f, /^\d$/.test(f) ? `Form ${f}` : f])]
    : [['all', 'All levels'], ['igcse', 'IGCSE'], ['alevel', 'AS & A Level']];

  return (
    <div className="space-y-4">
      <SoftCard className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <span className="font-semibold text-ink dark:text-slate-100">{series.title}</span>
        <span className="text-[var(--sp-muted)]">{internal ? `Forms ${forms.join(', ')}` : series.classes.map((c) => c.className).join(' · ')}</span>
        <span className="flex-1" />
        {series.published ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
            <CheckCircle2 className="h-3.5 w-3.5" /> pupils and parents can see this
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
            <EyeOff className="h-3.5 w-3.5" /> draft — staff only
          </span>
        )}
      </SoftCard>

      <div className="flex flex-wrap items-center gap-1.5" role="radiogroup" aria-label="Filter">
        {choices.map(([id, label]) => (
          <button key={id} type="button" role="radio" aria-checked={filter === id} onClick={() => setFilter(id)}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold ${filter === id
                    ? 'bg-ink text-white dark:bg-slate-100 dark:text-slate-900'
                    : 'border border-[var(--sp-line-2)] text-[var(--sp-body)] hover:bg-[var(--sp-card)]'}`}>
            {label}
          </button>
        ))}
      </div>

      {!days.length && (
        <SoftCard><p className="text-center text-sm text-[var(--sp-muted)]">No papers here in this series.</p></SoftCard>
      )}

      {days.map(({ date, sessions }) => {
        const past = sessions.every((s) => gaboroneInstant(s.date, s.window ? '23:59' : s.end || '23:59') < now);
        return (
          <SoftCard key={date} pad="" className={past ? 'opacity-60' : ''}>
            <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-[var(--sp-line)] px-5 py-2.5">
              <h3 className="text-sm font-bold text-ink dark:text-slate-100">{shortDay(date)}</h3>
              <p className="text-xs text-[var(--sp-muted)]">
                {filter === 'all'
                  ? `${sessions.reduce((n, s) => n + s.candidates, 0)} candidates`
                  : `${sessions.reduce((n, s) => n + s.papers.reduce((m, p) => m + p.candidates, 0), 0)} scripts`}
                {past ? ' · sat' : ''}
              </p>
            </header>
            <div className="divide-y divide-[var(--sp-line)]/60">
              {sessions.map((s) => (
                <div key={s.key} className="px-5 py-3">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--sp-muted)]">
                    {s.window || !s.session ? 'Test window' : sessionName(s.session, series.sessionLabels)}
                    {!s.window && s.start && !internal ? ` · session ${s.start}–${s.end}` : ''}
                  </p>
                  <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                    {s.papers.map((p) => (
                      <div key={`${p.code}|${p.title}`} className="block w-full rounded-lg border border-[var(--sp-line)] bg-[var(--sp-paper)] p-3 text-left">
                        <p className="min-w-0 text-sm">
                          <span className="font-bold tabular-nums text-ink dark:text-slate-100">{p.code}</span>{' '}
                          <span className="text-[var(--sp-body)]">{p.subject}</span>
                          {p.title && <span className="block truncate text-xs text-[var(--sp-muted)]">{p.title}</span>}
                        </p>
                        <p className="mt-2 flex items-center gap-1.5 text-sm font-semibold tabular-nums text-ink dark:text-slate-100">
                          <Clock className="h-3.5 w-3.5 text-[var(--sp-muted)]" aria-hidden />
                          {s.window ? 'time set on the day' : `${p.start}–${p.end}`}
                          {p.minutes ? <span className="text-xs font-normal text-[var(--sp-muted)]"> · {p.minutes} min</span> : null}
                        </p>
                        <p className="mt-1 text-xs text-[var(--sp-muted)]">
                          {p.candidates} script{p.candidates === 1 ? '' : 's'}{p.classes.length ? ` · ${p.classes.join(', ')}` : ''}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </SoftCard>
        );
      })}
    </div>
  );
}
